import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import securityConfig from "../../config/security";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user, token } = useAuth() as any;
  const location = useLocation();

  // Show loading during initial auth check AND when user is authenticated but user object is still null
  // This handles the case where TokenAuth → AuthContext is still processing
  const isAuthProcessing = isLoading || (isAuthenticated && !user);

  if (isAuthProcessing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          {/* <span className="text-gray-600">
            {isLoading ? 'Loading...' : 'Authenticating...'}
          </span> */}
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // If a token is present in URL, redirect to /auth to process it
    const search = new URLSearchParams(location.search);
    const token =
      search.get("token") ||
      search.get("access_token") ||
      (() => {
        if (!window.location.hash) return null;
        const hashParams = new URLSearchParams(
          window.location.hash.substring(1),
        );
        return hashParams.get("token");
      })();
    if (token) {
      return (
        <Navigate to={`/auth?token=${encodeURIComponent(token)}`} replace />
      );
    }

    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h2 className="text-3xl font-extrabold text-secondary">
              {t("auth.accessRequired")}
            </h2>
            <p className="mt-4 text-gray-600">
              {t("auth.accessRequiredDescription")}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {t("auth.accessRequiredDetails")}
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => window.close()}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              {t("common.closeWindow")}
            </button>
          </div>
          <div className="text-xs text-gray-400">
            {t("auth.epointDepositSystem")}
          </div>
        </div>
      </div>
    );
  }

  // Enforce dealer-only access with token fallback
  const isDealerFromProfile = user && user.role === "dealer";
  let isDealerFromToken = false;
  try {
    const storedToken =
      token || localStorage.getItem(securityConfig.TOKEN_STORAGE_KEY);
    if (storedToken) {
      const payload = JSON.parse(atob(storedToken.split(".")[1]));
      const payloadRole = payload?.role || payload?.userType;
      isDealerFromToken = payloadRole === "dealer";
    }
  } catch (e) {}

  if (!isDealerFromProfile && !isDealerFromToken) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md w-full space-y-8 text-center">
          <div>
            <h2 className="text-3xl font-extrabold text-secondary">
              {t("auth.accessDenied")}
            </h2>
            <p className="mt-4 text-gray-600">
              {t("auth.accessDeniedDescription")}
            </p>
            <p className="mt-2 text-sm text-gray-500">
              {t("auth.accessDeniedDetails")}
            </p>
          </div>
          <div className="mt-6">
            <button
              onClick={() => window.close()}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md text-sm font-medium"
            >
              {t("common.closeWindow")}
            </button>
          </div>
          <div className="text-xs text-gray-400">
            {t("auth.epointDepositSystem")}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
